import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { fmtUSDate } from "../lib/date";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Loader2, FileText, Check, X, Banknote, Inbox, Download, Trash2, ListChecks } from "lucide-react";
import { PAYMENT_METHODS, methodLabel } from "./VendorPaymentsDialog";
import { authenticatedDownload } from "../lib/pdf";

const STATUS_BADGE = {
  pending:  "bg-amber-100 text-amber-800 border border-amber-300",
  approved: "bg-blue-100 text-blue-800 border border-blue-300",
  paid:     "bg-emerald-100 text-emerald-800 border border-emerald-300",
  rejected: "bg-red-100 text-red-800 border border-red-300",
};

const STATUS_LABEL = {
  pending: "Pending review",
  approved: "Approved",
  paid: "Paid",
  rejected: "Rejected",
};

function fmt(n) {
  return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function VendorInvoicesInbox({ open, onClose, onChanged }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [payFor, setPayFor] = useState(null);
  const [bulkPayOpen, setBulkPayOpen] = useState(false);

  const load = async () => {
    try {
      const params = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/vendors/invoices${params}`);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
      setItems([]);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, filter]);

  const approve = async (inv) => {
    try {
      await api.post(`/vendors/${inv.vendor_id}/invoices/${inv.id}/approve`);
      toast.success("Invoice approved");
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to approve");
    }
  };

  const reject = async (inv) => {
    const reason = window.prompt("Reason for rejection (shared with vendor):", "");
    if (reason === null) return;
    try {
      await api.post(`/vendors/${inv.vendor_id}/invoices/${inv.id}/reject`, { reason });
      toast.success("Invoice rejected");
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to reject");
    }
  };

  const remove = async (inv) => {
    if (!window.confirm("Delete this invoice? Vendor will lose visibility.")) return;
    try {
      await api.delete(`/vendors/${inv.vendor_id}/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const downloadPdf = async (inv) => {
    if (!inv.invoice_pdf_path) return toast.info("No PDF attached.");
    await authenticatedDownload(
      `/vendors/invoices/${inv.id}/pdf`,
      inv.invoice_pdf_filename || `invoice-${inv.id.slice(0, 8)}.pdf`,
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="vendor-invoices-inbox">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-slate-700" /> Vendor invoice inbox
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="invoices-filter-bar">
            {["pending", "approved", "paid", "rejected", "all"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`invoices-filter-${f}`}
                className={`text-xs px-3 py-1.5 rounded-sm border ${
                  filter === f
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                {f === "all" ? "All" : STATUS_LABEL[f]}
              </button>
            ))}
            <div className="flex-1" />
            {(() => {
              // Count approved invoices currently visible (or in latest fetch).
              // We compute regardless of filter so the bulk button is discoverable
              // even when the user is viewing "All" or "Pending".
              const approvedItems = (items || []).filter((i) => i.status === "approved");
              if (approvedItems.length === 0) return null;
              const approvedTotal = approvedItems.reduce((s, i) => s + (i.amount || 0), 0);
              return (
                <button
                  onClick={() => setBulkPayOpen(true)}
                  data-testid="bulk-pay-btn"
                  className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-3 py-1.5 rounded-sm"
                  title="Pay all approved invoices in one batch"
                >
                  <ListChecks className="w-3.5 h-3.5" />
                  Pay all approved · {approvedItems.length} · {fmt(approvedTotal)}
                </button>
              );
            })()}
          </div>

          <div className="border border-slate-200 rounded-sm overflow-hidden">
            {items === null ? (
              <div className="p-8 flex items-center justify-center text-slate-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center text-slate-500" data-testid="invoices-empty">
                <Inbox className="w-8 h-8 mx-auto text-slate-300 mb-3" />
                <div className="font-semibold text-slate-700">No {filter === "all" ? "" : STATUS_LABEL[filter].toLowerCase()} invoices.</div>
                <div className="text-sm mt-1">When a vendor submits an invoice through their portal, it'll appear here.</div>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Vendor</th>
                    <th className="text-left px-4 py-3">Invoice #</th>
                    <th className="text-left px-4 py-3">Description</th>
                    <th className="text-right px-4 py-3">Amount</th>
                    <th className="text-left px-4 py-3 pl-6">Status</th>
                    <th className="text-right px-4 py-3"> </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50/40" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{fmtUSDate(inv.submitted_at)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{inv.vendor_name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">{inv.invoice_number || "—"}</td>
                      <td className="px-4 py-3 text-slate-700 truncate max-w-[220px]">{inv.description || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{fmt(inv.amount)}</td>
                      <td className="px-4 py-3 pl-6">
                        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${STATUS_BADGE[inv.status] || ""}`}>
                          {STATUS_LABEL[inv.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {inv.invoice_pdf_path && (
                          <button
                            onClick={() => downloadPdf(inv)}
                            data-testid={`invoice-pdf-${inv.id}`}
                            className="text-slate-500 hover:text-slate-900 p-1 mr-1"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        {inv.status === "pending" && (
                          <>
                            <button
                              onClick={() => approve(inv)}
                              data-testid={`invoice-approve-${inv.id}`}
                              className="text-blue-700 hover:text-blue-900 p-1 mr-1"
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => reject(inv)}
                              data-testid={`invoice-reject-${inv.id}`}
                              className="text-slate-500 hover:text-red-600 p-1 mr-1"
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {(inv.status === "pending" || inv.status === "approved") && (
                          <button
                            onClick={() => setPayFor(inv)}
                            data-testid={`invoice-pay-${inv.id}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50 px-2.5 py-1 rounded-sm"
                          >
                            <Banknote className="w-3.5 h-3.5" /> Pay
                          </button>
                        )}
                        {inv.status !== "paid" && (
                          <button
                            onClick={() => remove(inv)}
                            data-testid={`invoice-delete-${inv.id}`}
                            className="text-slate-400 hover:text-red-600 p-1 ml-1"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter>
            <button type="button" className="btn-outline-dark" onClick={onClose} data-testid="invoices-inbox-close">Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {payFor && (
        <PayInvoiceDialog
          invoice={payFor}
          onClose={() => setPayFor(null)}
          onPaid={async () => {
            setPayFor(null);
            await load();
            if (onChanged) onChanged();
          }}
        />
      )}

      {bulkPayOpen && (
        <BulkPayDialog
          approvedItems={(items || []).filter((i) => i.status === "approved")}
          onClose={() => setBulkPayOpen(false)}
          onPaid={async () => {
            setBulkPayOpen(false);
            // After bulk pay, jump to the Paid filter so the user sees their work.
            setFilter("paid");
            await load();
            if (onChanged) onChanged();
          }}
        />
      )}
    </>
  );
}

// --- Bulk pay-all-approved dialog. Calls /vendors/invoices/bulk-pay which
// creates one vendor_payments row + status flip + status email per invoice
// in a single transaction. For check method, the user provides a starting
// check number and the backend auto-increments. ---
function BulkPayDialog({ approvedItems, onClose, onPaid }) {
  const total = approvedItems.reduce((s, i) => s + (i.amount || 0), 0);
  const [form, setForm] = useState({
    payment_method: "check",
    check_number_start: "",
    paid_at: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const showCheck = form.payment_method === "check" || form.payment_method === "money_order";

  const submit = async (e) => {
    e.preventDefault();
    if (showCheck && !form.check_number_start.trim()) {
      return toast.error("Enter a starting check number — we'll auto-increment from there.");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/vendors/invoices/bulk-pay", {
        payment_method: form.payment_method,
        check_number_start: form.check_number_start,
        paid_at: form.paid_at,
        notes: form.notes,
      });
      const dollars = `$${(data?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      toast.success(`Paid ${data?.paid_count || 0} invoices · ${dollars}`);
      if (onPaid) await onPaid();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk pay failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="bulk-pay-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-emerald-700" /> Pay all approved invoices
          </DialogTitle>
        </DialogHeader>

        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 mb-3 text-sm">
          <div className="font-semibold text-emerald-900">
            {approvedItems.length} approved invoice{approvedItems.length === 1 ? "" : "s"} · {fmt(total)}
          </div>
          <div className="text-xs text-emerald-800 mt-1">
            Each invoice will be paid using the same method below. Vendors will receive a "Payment sent" email automatically.
          </div>
        </div>

        <details className="border border-slate-200 rounded-sm p-3 mb-3 text-sm">
          <summary className="cursor-pointer text-slate-700 font-semibold">
            Review {approvedItems.length} invoice{approvedItems.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-2 space-y-1.5 max-h-48 overflow-y-auto" data-testid="bulk-pay-preview">
            {approvedItems.map((i) => (
              <li key={i.id} className="flex items-center justify-between text-xs">
                <span className="text-slate-700">{i.vendor_name} · <span className="font-mono">{i.invoice_number || i.id.slice(0, 8)}</span></span>
                <span className="font-mono font-semibold text-slate-900">{fmt(i.amount)}</span>
              </li>
            ))}
          </ul>
        </details>

        <form onSubmit={submit} className="space-y-3">
          <Field label="Payment method (applied to all)">
            <select
              className="input-clean w-full"
              data-testid="bulk-pay-method"
              value={form.payment_method}
              onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
            >
              {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            {showCheck ? (
              <Field label="Starting check #">
                <input
                  type="text"
                  className="input-clean w-full"
                  data-testid="bulk-pay-check-start"
                  value={form.check_number_start}
                  onChange={(e) => setForm({ ...form, check_number_start: e.target.value })}
                  placeholder="e.g. 1042"
                  required
                />
                <div className="text-[11px] text-slate-500 mt-1">Auto-increments per invoice (1042, 1043, 1044…).</div>
              </Field>
            ) : (
              <div /> /* spacer */
            )}
            <Field label="Date paid">
              <input
                required
                type="date"
                className="input-clean w-full"
                data-testid="bulk-pay-date"
                value={form.paid_at}
                onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Notes (applied to all)">
            <textarea
              rows={2}
              maxLength={500}
              className="input-clean w-full text-sm"
              data-testid="bulk-pay-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Memo / batch reference"
            />
          </Field>
          <DialogFooter>
            <button type="button" className="btn-outline-dark" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              type="submit"
              disabled={busy || approvedItems.length === 0}
              data-testid="bulk-pay-confirm"
              className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-sm disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
              Pay {approvedItems.length} · {fmt(total)}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// --- Inline pay-invoice dialog: uses /invoices/{iid}/pay so the invoice is
// flipped to status=paid AND a vendor_payments row is created in one shot. ---
function PayInvoiceDialog({ invoice, onClose, onPaid }) {
  const [form, setForm] = useState({
    amount: invoice.amount,
    payment_method: "check",
    check_number: "",
    paid_at: new Date().toISOString().slice(0, 10),
    notes: `Invoice ${invoice.invoice_number || invoice.id.slice(0, 8)}`,
  });
  const [busy, setBusy] = useState(false);
  const showCheck = form.payment_method === "check" || form.payment_method === "money_order";

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    setBusy(true);
    try {
      await api.post(`/vendors/${invoice.vendor_id}/invoices/${invoice.id}/pay`, {
        amount: amt,
        payment_method: form.payment_method,
        check_number: form.check_number,
        paid_at: form.paid_at,
        notes: form.notes,
      });
      toast.success("Invoice paid");
      if (onPaid) await onPaid();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to pay");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="pay-invoice-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-emerald-700" /> Pay invoice
          </DialogTitle>
        </DialogHeader>

        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-3 text-sm">
          <div className="font-semibold text-slate-900">{invoice.vendor_name}</div>
          <div className="text-xs text-slate-500 font-mono mt-0.5">
            Invoice {invoice.invoice_number || invoice.id.slice(0, 8)} · {fmt(invoice.amount)}
          </div>
          {invoice.description && <div className="text-xs text-slate-600 mt-1">{invoice.description}</div>}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount paid (USD)">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                <input
                  required
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="input-clean w-full pl-7"
                  data-testid="pay-invoice-amount"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                />
              </div>
            </Field>
            <Field label="Payment method">
              <select
                className="input-clean w-full"
                data-testid="pay-invoice-method"
                value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
              >
                {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {showCheck ? (
              <Field label="Check number">
                <input
                  type="text"
                  className="input-clean w-full"
                  data-testid="pay-invoice-check-number"
                  value={form.check_number}
                  onChange={(e) => setForm({ ...form, check_number: e.target.value })}
                  placeholder="e.g. 1042"
                />
              </Field>
            ) : (
              <Field label="Reference (optional)">
                <input
                  type="text"
                  className="input-clean w-full"
                  data-testid="pay-invoice-reference"
                  value={form.check_number}
                  onChange={(e) => setForm({ ...form, check_number: e.target.value })}
                />
              </Field>
            )}
            <Field label="Date paid">
              <input
                required
                type="date"
                className="input-clean w-full"
                data-testid="pay-invoice-date"
                value={form.paid_at}
                onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Notes">
            <textarea
              rows={2}
              maxLength={500}
              className="input-clean w-full text-sm"
              data-testid="pay-invoice-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Field>
          <DialogFooter>
            <button type="button" className="btn-outline-dark" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              type="submit"
              disabled={busy}
              data-testid="pay-invoice-submit"
              className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-sm disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
              Pay {fmt(parseFloat(form.amount) || 0)}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
